// React context for the "currently active workbench session".
//
// The Session panel sets/clears it; other Workbench pages read it.
// One context per browser tab — multi-session workflows are deferred
// (the alpha workbench is single-session at a time per v2.1 §5).

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { CoframeApiClient } from "../api/client";
import type { SessionInfo } from "../api/types";

interface SessionContextValue {
  client: CoframeApiClient;
  session: SessionInfo | null;
  setSession: (info: SessionInfo | null) => void;
  /** Re-fetch the session info from the backend (after a state-changing op). */
  refreshSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  children,
  client,
}: {
  children: ReactNode;
  client?: CoframeApiClient;
}) {
  const [session, setSession] = useState<SessionInfo | null>(null);

  // Stable apiClient across renders. If the caller passes one in, honour
  // that (so test setups can inject a mock). Otherwise construct once and
  // hold it in a ref-backed memo. Recreating it on each render would
  // change the `client` reference handed to consumers — and any useEffect
  // depending on `client` would re-fire every render. That's how we got
  // the infinite-loop "screen shake" on the workbench pages.
  const apiClient = useMemo(
    () => client ?? new CoframeApiClient(),
    [client],
  );

  // Keep the latest session in a ref so refreshSession's identity stays
  // stable. (Putting `session` in the useCallback deps would make every
  // setSession() create a new refreshSession ref — same footgun.)
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const refreshSession = useCallback(async () => {
    const current = sessionRef.current;
    if (current === null) return;
    const fresh = await apiClient.getSession(current.session_id);
    setSession(fresh);
  }, [apiClient]);

  const value = useMemo(
    () => ({ client: apiClient, session, setSession, refreshSession }),
    [apiClient, session, refreshSession],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error("useSession must be used inside <SessionProvider>.");
  }
  return ctx;
}
